import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

interface PaperHit {
  id: string;
  title: string;
  authors: string;
}

interface ApiPaper {
  id: string;
  title: string;
  authors: unknown; // list of strings or dicts from the backend
}

interface PapersListResponse {
  papers: ApiPaper[];
  total: number;
  offset: number;
  limit: number;
}

function formatAuthors(raw: unknown): string {
  if (!Array.isArray(raw)) return "";
  const names = raw
    .map((a) => {
      if (typeof a === "string") return a;
      if (a && typeof a === "object" && "name" in a) return String((a as { name?: unknown }).name ?? "");
      return "";
    })
    .filter(Boolean);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} et al.`;
}

interface Props {
  conceptId: string;
  onPick: (p: PaperHit) => void;
}

export default function PaperTypeahead({ conceptId, onPick }: Props) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching, error } = useQuery<PapersListResponse>({
    queryKey: ["concept-papers-search", conceptId, debounced],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/papers`, {
          searchParams: { search: debounced, limit: 10 },
        })
        .json<PapersListResponse>(),
    enabled: debounced.length > 0,
    retry: false,
  });

  const papers = data?.papers ?? [];

  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by title or author…"
        className="w-full border rounded p-2 text-sm bg-background text-foreground"
        autoFocus
      />
      <div className="mt-2 max-h-64 overflow-y-auto">
        {isFetching && (
          <div className="text-xs text-muted-foreground p-2">Searching…</div>
        )}
        {!isFetching && error && (
          <div className="text-xs text-red-600 dark:text-red-400 p-2">
            Search failed: {error instanceof Error ? error.message : "unknown error"}
          </div>
        )}
        {!isFetching && !error && debounced.length > 0 && papers.length === 0 && (
          <div className="text-xs text-muted-foreground p-2">
            No papers match "{debounced}".
          </div>
        )}
        {papers.map((p) => (
          <button
            key={p.id}
            className="block w-full text-left p-2 text-sm hover:bg-accent hover:text-accent-foreground rounded"
            onClick={() =>
              onPick({ id: p.id, title: p.title, authors: formatAuthors(p.authors) })
            }
          >
            <div className="font-medium">{p.title}</div>
            <div className="text-xs text-muted-foreground">{formatAuthors(p.authors)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
